import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, Webhook as WebhookIcon, Copy, Check } from "lucide-react";
import { WebhookEventType } from "@finora/types";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const EVENT_LABELS: Record<WebhookEventType, string> = {
  FISCALIZATION_COMPLETED: "fiscalization.completed",
  FISCALIZATION_FAILED: "fiscalization.failed",
  DOCUMENT_CANCELLED: "document.cancelled",
  DOCUMENT_REFUNDED: "document.refunded",
  IPS_PAYMENT_PAID: "ips_payment.paid",
  IPS_PAYMENT_FAILED: "ips_payment.failed",
};

function SecretRevealModal({ secret, onClose }: { secret: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Webhook secret</h2>
        </div>
        <div className="space-y-3 px-5 py-5">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Koristite ovo za proveru HMAC-SHA256 potpisa u X-HAfiscal-Signature headeru (stariji
            integracije proveravaju X-Finora-Signature - potpis je isti). Prikazuje se samo sada.
          </p>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-app-bg px-3 py-2 font-mono text-sm break-all">
            {secret}
          </div>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(secret).catch(() => undefined);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="flex items-center gap-1.5 text-sm text-brand hover:underline"
          >
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {copied ? "Kopirano" : "Kopiraj secret"}
          </button>
        </div>
        <div className="flex justify-end border-t border-border px-5 py-4">
          <Button onClick={onClose}>U redu</Button>
        </div>
      </div>
    </div>
  );
}

function NewWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: (secret: string) => void }) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEventType[]>([]);

  const save = useMutation({
    mutationFn: () => api.createCloudApiWebhook({ url, events }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["cloud-api-webhooks"] });
      onCreated(result.secret);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Novo webhook odredište</h2>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">URL</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://vas-sistem.rs/webhook/hafiscal"
              className="h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Eventi</label>
            <div className="space-y-1.5">
              {Object.values(WebhookEventType).map((ev) => (
                <label key={ev} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={events.includes(ev)}
                    onChange={(e) =>
                      setEvents((prev) =>
                        e.target.checked ? [...prev, ev] : prev.filter((x) => x !== ev),
                      )
                    }
                    className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                  />
                  <span className="font-mono text-xs">{EVENT_LABELS[ev]}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Firma sme imati najviše 1 aktivan webhook ukupno - vidi
              razgovor "tacno 1 webhook po API licenci". */}
          {save.isError && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {save.error instanceof ApiError ? save.error.response.message : "Čuvanje nije uspelo."}
            </p>
          )}
        </div>
        <div className="flex gap-3 border-t border-border px-5 py-4">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Otkaži
          </Button>
          <Button
            className="flex-1"
            disabled={!url || events.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            Sačuvaj
          </Button>
        </div>
      </div>
    </div>
  );
}

export function WebhooksTab() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [revealSecret, setRevealSecret] = useState<string | null>(null);
  const { data: webhooks, isLoading } = useQuery({
    queryKey: ["cloud-api-webhooks"],
    queryFn: () => api.listCloudApiWebhooks(),
  });

  const disable = useMutation({
    mutationFn: (id: string) => api.disableCloudApiWebhook(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cloud-api-webhooks"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Novo webhook odredište
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !webhooks || webhooks.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={WebhookIcon}
              title="Nema webhook odredišta"
              description="Dodajte URL koji HAFiskal treba da obavesti o događajima (fiskalizacija, otkazivanje...)."
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>URL</th>
                <th>Eventi</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {webhooks.map((w) => (
                <tr key={w.id}>
                  <td className="max-w-xs truncate font-mono text-xs text-slate-700">{w.url}</td>
                  <td className="text-xs text-slate-500">
                    {w.events.map((e) => EVENT_LABELS[e]).join(", ")}
                  </td>
                  <td>
                    {w.status === "ACTIVE" ? (
                      <span className="text-xs text-emerald-600">Aktivan</span>
                    ) : (
                      <span className="text-xs text-red-500">Deaktiviran</span>
                    )}
                  </td>
                  <td>
                    {w.status === "ACTIVE" && (
                      <Button size="sm" variant="danger" onClick={() => disable.mutate(w.id)}>
                        Deaktiviraj
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <NewWebhookModal
          onClose={() => setModalOpen(false)}
          onCreated={(secret) => {
            setModalOpen(false);
            setRevealSecret(secret);
          }}
        />
      )}
      {revealSecret && <SecretRevealModal secret={revealSecret} onClose={() => setRevealSecret(null)} />}
    </div>
  );
}
